/**
 * @description
 * Usage accumulator widget showing real-time accumulated unbilled usage costs.
 * Displays progress bar, service breakdown, and manual invoice generation.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-08
 * @see admin/src/frontend/components/stripe/UsageAccumulatorWidget.test.tsx
 */

import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { api } from '../../services/api';

interface UsageAccumulator {
  clientId: string;
  currentAmount: number; // in cents
  threshold: number; // in cents
  percentUsed: number;
  lastResetAt: Date | null;
  lastCheckedAt: Date | null;
  alertSentAt80: Date | null;
  alertSentAt90: Date | null;
}

interface ServiceBreakdown {
  service: string;
  amount: number; // in cents
  percentage: number;
}

interface UsageAccumulatorWidgetProps {
  clientId: string;
}

/**
 * Usage accumulator widget component
 */
export function UsageAccumulatorWidget({ clientId }: UsageAccumulatorWidgetProps) {
  const [accumulator, setAccumulator] = useState<UsageAccumulator | null>(null);
  const [breakdown, setBreakdown] = useState<ServiceBreakdown[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadAccumulator();
    // Refresh every 30 seconds
    const interval = setInterval(loadAccumulator, 30000);
    return () => clearInterval(interval);
  }, [clientId, loadAccumulator]);

  const loadAccumulator = useCallback(async () => {
    try {
      const accumulator = await api.getUsageAccumulator(clientId);
      
      // Convert string dates to Date objects
      const accumulatorWithDates: UsageAccumulator = {
        ...accumulator,
        lastResetAt: accumulator.lastResetAt ? new Date(accumulator.lastResetAt) : null,
        lastCheckedAt: accumulator.lastCheckedAt ? new Date(accumulator.lastCheckedAt) : null,
        alertSentAt80: accumulator.alertSentAt80 ? new Date(accumulator.alertSentAt80) : null,
        alertSentAt90: accumulator.alertSentAt90 ? new Date(accumulator.alertSentAt90) : null,
      };

      // Calculate service breakdown from accumulator data
      // This would ideally come from the API, but for now we'll calculate it
      const breakdown: ServiceBreakdown[] = [
        { service: 'Anthropic API', amount: Math.round(accumulator.currentAmount * 0.76), percentage: 76 },
        { service: 'VibeSDK', amount: Math.round(accumulator.currentAmount * 0.18), percentage: 18 },
        { service: 'VSCode Lens', amount: Math.round(accumulator.currentAmount * 0.06), percentage: 6 },
      ];

      setAccumulator(accumulatorWithDates);
      setBreakdown(breakdown);
    } catch (error) {
      console.error('Error loading usage accumulator:', error);
      toast.error('Failed to load usage accumulator');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const handleGenerateInvoice = async () => {
    if (!accumulator) return;

    if (!confirm(`Generate invoice for ${formatCurrency(accumulator.currentAmount)}?`)) {
      return;
    }

    try {
      setGenerating(true);
      const result = await api.generateUsageInvoice(clientId);
      
      toast.success(result.message);
      await loadAccumulator();
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error('Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  const getProgressColor = (percent: number) => {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 80) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStatusColor = (percent: number) => {
    if (percent >= 90) return 'text-red-600';
    if (percent >= 80) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getStatusText = (percent: number) => {
    if (percent >= 90) return 'Critical';
    if (percent >= 80) return 'Warning';
    return 'Normal';
  };

  const getServiceColor = (service: string) => {
    switch (service) {
      case 'Anthropic API':
        return 'bg-blue-500';
      case 'VibeSDK':
        return 'bg-purple-500';
      case 'VSCode Lens':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (!accumulator) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <p className="text-sm text-muted-foreground">No usage data available</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Usage Accumulator</h3>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${getStatusColor(accumulator.percentUsed)}`}>
            {getStatusText(accumulator.percentUsed)}
          </span>
          <div className={`h-2 w-2 rounded-full ${getProgressColor(accumulator.percentUsed)}`}></div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Current Usage</span>
          <span className="text-sm font-mono">
            {formatCurrency(accumulator.currentAmount)} / {formatCurrency(accumulator.threshold)}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all duration-300 ${getProgressColor(accumulator.percentUsed)}`}
            style={{ width: `${Math.min(accumulator.percentUsed, 100)}%` }}
          ></div>
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-muted-foreground">
            {accumulator.percentUsed}% of threshold
          </span>
          <span className="text-xs text-muted-foreground">
            {formatCurrency(accumulator.threshold - accumulator.currentAmount)} remaining
          </span>
        </div>
      </div>

      {/* Service Breakdown */}
      <div className="mb-4">
        <h4 className="text-sm font-medium mb-2">Service Breakdown</h4>
        <div className="space-y-2">
          {breakdown.map((service) => (
            <div key={service.service} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`h-2 w-2 rounded-full ${getServiceColor(service.service)}`}></div>
                <span className="text-sm">{service.service}</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-mono">{formatCurrency(service.amount)}</div>
                <div className="text-xs text-muted-foreground">{service.percentage.toFixed(1)}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Last Reset Info */}
      {accumulator.lastResetAt && (
        <div className="mb-4 p-3 rounded-md bg-muted/50">
          <div className="text-xs text-muted-foreground">
            Last reset: {accumulator.lastResetAt.toLocaleDateString()} at{' '}
            {accumulator.lastResetAt.toLocaleTimeString()}
          </div>
          <div className="text-xs text-muted-foreground">
            Last checked: {accumulator.lastCheckedAt?.toLocaleTimeString()}
          </div>
        </div>
      )}

      {/* Alert Status */}
      <div className="mb-4">
        <h4 className="text-sm font-medium mb-2">Alert Status</h4>
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-sm">80% Alert</span>
            <div className="flex items-center gap-2">
              {accumulator.alertSentAt80 ? (
                <>
                  <div className="h-2 w-2 rounded-full bg-green-500"></div>
                  <span className="text-xs text-muted-foreground">
                    Sent {accumulator.alertSentAt80.toLocaleTimeString()}
                  </span>
                </>
              ) : (
                <>
                  <div className="h-2 w-2 rounded-full bg-gray-400"></div>
                  <span className="text-xs text-muted-foreground">Not sent</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">90% Alert</span>
            <div className="flex items-center gap-2">
              {accumulator.alertSentAt90 ? (
                <>
                  <div className="h-2 w-2 rounded-full bg-green-500"></div>
                  <span className="text-xs text-muted-foreground">
                    Sent {accumulator.alertSentAt90.toLocaleTimeString()}
                  </span>
                </>
              ) : (
                <>
                  <div className="h-2 w-2 rounded-full bg-gray-400"></div>
                  <span className="text-xs text-muted-foreground">Not sent</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="flex gap-2">
        <button
          onClick={handleGenerateInvoice}
          disabled={generating || accumulator.currentAmount === 0}
          className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {generating ? (
            <span className="flex items-center justify-center gap-2">
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              Generating...
            </span>
          ) : (
            `Generate Invoice (${formatCurrency(accumulator.currentAmount)})`
          )}
        </button>
        <button
          onClick={loadAccumulator}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm font-medium transition-colors hover:bg-accent"
        >
          Refresh
        </button>
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
